import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import MUITable from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import { API_ROLE } from '../../api/role';
import RoleRow from './components/RoleRow';

import s from './styles.module.scss';
import { RoleCreatePayload, RoleTableData } from './types';

const RowsTable = ({ data }: { data: RoleTableData[] }) => {
  const [rows, setRows] = useState<RoleTableData[]>(data);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleTableData | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<RoleCreatePayload>({ name: '', code: 0 });
  const [errors, setErrors] = useState<{ name?: string; code?: string }>({});

  const systemCodes = new Set([4000, 2000, 5001, 5002, 5003, 5004, 5005, 5006]);

  const validate = (payload: RoleCreatePayload) => {
    const nextErrors: { name?: string; code?: string } = {};
    if (!payload.name || payload.name.trim().length < 2) nextErrors.name = 'Name must be at least 2 characters';
    if (!Number.isInteger(payload.code) || payload.code < 1) nextErrors.code = 'Code must be a positive integer';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const openCreate = () => {
    setSelectedRole(null);
    setForm({ name: '', code: 0 });
    setErrors({});
    setShowCreate(true);
  };

  const openEdit = (role: RoleTableData) => {
    setSelectedRole(role);
    setForm({ name: role.name, code: role.code });
    setErrors({});
    setShowEdit(true);
  };

  const openDelete = (role: RoleTableData) => {
    setSelectedRole(role);
    setShowDelete(true);
  };

  const handleCreate = async () => {
    if (!validate(form)) return;
    setSaving(true);
    const success = await API_ROLE.create({ name: form.name.trim(), code: Number(form.code) });
    if (success) {
      const latest = await API_ROLE.getAll();
      setRows(latest.map((role) => ({ id: role.id, name: role.name, code: role.code })));
      setShowCreate(false);
    }
    setSaving(false);
  };

  const handleEdit = async () => {
    if (!selectedRole) return;
    if (!validate(form)) return;
    setSaving(true);
    const success = await API_ROLE.update(selectedRole.id, { name: form.name.trim(), code: Number(form.code) });
    if (success) {
      const latest = await API_ROLE.getAll();
      setRows(latest.map((role) => ({ id: role.id, name: role.name, code: role.code })));
      setShowEdit(false);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selectedRole) return;
    setSaving(true);
    const success = await API_ROLE.remove(selectedRole.id);
    if (success) {
      setRows((prev) => prev.filter((role) => role.id !== selectedRole.id));
      setShowDelete(false);
    }
    setSaving(false);
  };

  return (
    <TableContainer className={s.container}>
      <MUITable>
        <TableHead className={s.header}>
          <TableRow>
            <TableCell className={s.cell}>Role Name</TableCell>
            <TableCell className={s.cell}>Code</TableCell>
            <TableCell className={s.cell}>Type</TableCell>
            <TableCell className={s.cell}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <RoleRow
              key={row.id}
              data={row}
              onEdit={openEdit}
              onDelete={openDelete}
              canEdit={!systemCodes.has(row.code)}
              canDelete={!systemCodes.has(row.code)}
            />
          ))}
        </TableBody>
      </MUITable>

      <Box className={s.bottom}>
        <Button variant='contained' sx={{ alignSelf: 'flex-start' }} onClick={openCreate}>
          Add Role
        </Button>
      </Box>

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} fullWidth maxWidth='xs'>
        <DialogTitle>Create Role</DialogTitle>
        <DialogContent>
          <TextField
            label='Role Name'
            fullWidth
            margin='dense'
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            error={Boolean(errors.name)}
            helperText={errors.name}
          />
          <TextField
            label='Role Code'
            fullWidth
            margin='dense'
            type='number'
            value={form.code || ''}
            onChange={(e) => setForm((prev) => ({ ...prev, code: Number(e.target.value) }))}
            error={Boolean(errors.code)}
            helperText={errors.code}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} variant='contained'>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showEdit} onClose={() => setShowEdit(false)} fullWidth maxWidth='xs'>
        <DialogTitle>Edit Role</DialogTitle>
        <DialogContent>
          <TextField
            label='Role Name'
            fullWidth
            margin='dense'
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            error={Boolean(errors.name)}
            helperText={errors.name}
          />
          <TextField
            label='Role Code'
            fullWidth
            margin='dense'
            type='number'
            value={form.code || ''}
            onChange={(e) => setForm((prev) => ({ ...prev, code: Number(e.target.value) }))}
            error={Boolean(errors.code)}
            helperText={errors.code}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEdit(false)}>Cancel</Button>
          <Button onClick={handleEdit} disabled={saving} variant='contained'>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showDelete} onClose={() => setShowDelete(false)} fullWidth maxWidth='xs'>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          Are you sure you want to delete <strong>{selectedRole?.name}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDelete(false)}>Cancel</Button>
          <Button color='error' onClick={handleDelete} disabled={saving} variant='contained'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </TableContainer>
  );
};

export default RowsTable;
